import React, { useState } from 'react';
import { 
  FileText, 
  Download, 
  Trash2, 
  Plus, 
  Upload,
  X,
  File
} from 'lucide-react';
import { uploadDocument, deleteDocument, getDocumentUrl } from '../../lib/supabase';

const DocumentList = ({ clientId, documents, onDocumentChange }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
  
    setIsUploading(true);
    setUploadError(null);
  
    try {
      // Create a more descriptive file object for Excel files
      const isExcel = /\.(xlsx|xls|xlsm|xlsb|xltx|xltm|xlt)$/i.test(file.name);
      
      // For Excel files, make sure to preserve the original file name and set the correct MIME type
      let fileToUpload = file;
      
      // If it's an Excel file but the browser didn't recognize the MIME type properly
      if (isExcel && !file.type.includes('excel') && !file.type.includes('spreadsheet')) {
        // Create a new File object with the correct MIME type
        const correctMimeType = file.name.toLowerCase().endsWith('xlsx') 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/vnd.ms-excel';
        
        // Create a new File object with the correct MIME type but preserve the original name
        fileToUpload = new File(
          [file], 
          file.name,
          { type: correctMimeType }
        );
      }
      
      await uploadDocument(fileToUpload, clientId);
      onDocumentChange();
      event.target.value = '';
    } catch (err) {
      console.error('Error uploading document:', err);
      setUploadError('Failed to upload document. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      await deleteDocument(documentId);
      onDocumentChange(); // Trigger refresh of documents list
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const handleDownload = async (doc) => {
    try {
      // Get the signed URL from Supabase
      const url = await getDocumentUrl(doc.file_path);
      
      // Check if it's an Excel file by extension or MIME type
      // const isExcel = doc.name?.match(/\.(xlsx|xls|xlsm|xlsb|xltx|xltm|xlt)$/i) || 
      //                doc.file_type?.includes('excel') || 
      //                doc.file_type?.includes('spreadsheet');
      
      // For Excel files and most other files, use the fetch and blob approach for better filename control
      try {
        // Fetch the file as a blob
        const response = await fetch(url);
        const blob = await response.blob();
        
        // Create a blob URL and download it with the original filename
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = doc.name; // Use the original filename stored in the database
        
        // Append to body, click to trigger download, then clean up
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the blob URL
        window.URL.revokeObjectURL(blobUrl);
      } catch (fetchError) {
        console.error('Error with fetch download:', fetchError);
        
        // Fallback to direct download if fetch fails
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.name;
        link.target = '_blank';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error downloading document:', err);
      alert('Failed to download document. Please try again.');
    }
  };

  const getFileIcon = (fileType, fileName) => {
    // First check for Excel file extensions regardless of MIME type
    if (fileName && /\.(xlsx|xls|xlsm|xlsb|xltx|xltm|xlt)$/i.test(fileName)) {
      return <File className="h-5 w-5 text-green-600" />;
    }
    
    // Then check MIME types
    if (fileType?.includes('spreadsheet') || 
        fileType?.includes('excel') || 
        fileType?.includes('sheet') || 
        fileType?.includes('ms-excel') ||
        fileType?.includes('application/vnd.openxmlformats-officedocument.spreadsheetml')) {
      return <File className="h-5 w-5 text-green-600" />;
    }
    
    // Other file types remain unchanged
    if (fileType?.includes('pdf')) return <File className="h-5 w-5 text-red-500" />;
    if (fileType?.includes('image')) return <File className="h-5 w-5 text-blue-500" />;
    if (fileType?.includes('word') || fileType?.includes('document')) return <File className="h-5 w-5 text-blue-600" />;
    if (fileType?.includes('presentation') || fileType?.includes('powerpoint')) return <File className="h-5 w-5 text-orange-500" />;
    
    return <FileText className="h-5 w-5 text-gray-500" />;
  };
  
  // Enhanced function to get file extension for display
  const getFileExtension = (fileName, fileType) => {
    // First try to get extension from filename
    if (fileName) {
      const extension = fileName.split('.').pop().toLowerCase();
      
      // Handle Office file extensions specifically
      const officeExtMap = {
        'xlsx': 'Excel',
        'xls': 'Excel',
        'xlsm': 'Excel (Macro)',
        'xlsb': 'Excel Binary',
        'xltx': 'Excel Template',
        'xltm': 'Excel Template (Macro)',
        'docx': 'Word',
        'doc': 'Word',
        'pptx': 'PowerPoint',
        'ppt': 'PowerPoint',
        'pdf': 'PDF'
      };
      
      if (officeExtMap[extension]) {
        return officeExtMap[extension];
      }
      
      return extension.toUpperCase();
    }
    
    // Fallback to MIME type if filename doesn't have a recognizable extension
    if (fileType) {
      if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType.includes('sheet')) {
        return 'Excel';
      }
      if (fileType.includes('word') || fileType.includes('document')) {
        return 'Word';
      }
      if (fileType.includes('presentation') || fileType.includes('powerpoint')) {
        return 'PowerPoint';
      }
      if (fileType.includes('pdf')) {
        return 'PDF';
      }
      
      return fileType.split('/')[1] || 'Unknown';
    }
    
    return 'Unknown';
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 border border-teal-500">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Documents</h2>
        <div>
          <input
            type="file"
            onChange={handleFileUpload}
            className="hidden"
            id="document-upload"
            disabled={isUploading}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png" // Specify accepted file types
          />
          <label
            htmlFor="document-upload"
            className={`flex items-center justify-center px-4 py-2 rounded-md 
              ${isUploading 
                ? 'bg-gray-100 cursor-not-allowed' 
                : 'bg-teal-600 hover:bg-teal-700 cursor-pointer'}
              text-white transition-colors`}
          >
            {isUploading ? (
              <>
                <Upload className="h-5 w-5 mr-2 animate-pulse" />
                Uploading...
              </>
            ) : (
              <>
                <Plus className="h-5 w-5 mr-2" />
                Upload Document
              </>
            )}
          </label>
        </div>
      </div>

      {uploadError && (
        <div className="mb-4 bg-red-50 text-red-500 p-3 rounded-md flex items-center justify-between">
          <span className="flex items-center">
            <X className="h-5 w-5 mr-2" />
            {uploadError}
          </span>
          <button 
            onClick={() => setUploadError(null)}
            className="text-red-700 hover:text-red-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="space-y-2">
        {documents?.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No documents uploaded yet
          </div>
        )}

        {documents?.map((doc) => (
            <div 
            key={doc.id} 
            className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
          >
            <div className="flex items-center flex-1 min-w-0">
              {getFileIcon(doc.file_type, doc.name)}
              <span className="ml-2 text-sm font-medium text-gray-700 truncate">
                {doc.name}
              </span>
              <span className="ml-2 text-xs text-gray-500">
                ({getFileExtension(doc.name, doc.file_type)})
              </span>
              <span className="ml-2 text-xs text-gray-500">
                {new Date(doc.created_at).toLocaleDateString()}
              </span>
            </div>
            
            <div className="flex items-center ml-4 space-x-2">
              <button
                onClick={() => handleDownload(doc)}
                className="p-1 text-teal-600 hover:text-teal-700 transition-colors"
                title="Download"
              >
                <Download className="h-5 w-5" />
              </button>
              <button
                onClick={() => {
                  setSelectedDocId(doc.id);
                  setConfirmOpen(true);
                }}
                className="p-1 text-red-500 hover:text-red-700 transition-colors"
                title="Delete"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {isConfirmOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          {/* Overlay */}
          <div className="fixed inset-0 bg-black opacity-50"></div>

          {/* Modal Content */}
          <div className="bg-white p-6 rounded shadow-lg relative z-10 max-w-sm w-full">
            <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
            <p className="mb-4">Are you sure you want to delete this document?</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDeleteDocument(selectedDocId);
                  setConfirmOpen(false);
                  setSelectedDocId(null);
                }}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentList;